import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { identifyUser } from "@/lib/posthog/client";
import { track } from "@/lib/posthog/events";
import { parseAuthError } from "@/utils/authErrorParser";
import { detectEmailVerificationEnabled, getVerificationRedirectUrl } from "@/utils/authUtils";
import { logger } from "@/utils/logger";
import { normalizeName } from "@/utils/sanitization";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n/LanguageContext";

export interface SignupFormData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface SignupOptions {
  /**
   * Destination after a successful, fully-confirmed signup (default: /create-organization).
   * Also used as the `next=` target on the email verification callback URL so that
   * after the user clicks the verification link they land at the same place.
   */
  redirectUrl?: string;
  /** If true, don't navigate after signup — let the caller handle it. */
  skipNavigation?: boolean;
}

export function useRobustSignup() {
  const [isLoading, setIsLoading] = useState(false);
  const [showVerificationPending, setShowVerificationPending] = useState(false);
  const [userEmail, setUserEmail] = useState("");

  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();

  const signUp = async (formData: SignupFormData, options?: SignupOptions) => {
    setIsLoading(true);

    try {
      const { email, password, firstName, lastName } = formData;
      const normalizedFirstName = normalizeName(firstName);
      const normalizedLastName = normalizeName(lastName);

      if (!normalizedFirstName || !normalizedLastName) {
        toast({
          variant: "destructive",
          title: t.forms.validation.error,
          description: t.forms.validation.fieldsRequired,
        });
        return { success: false, needsVerification: false };
      }

      const postSignupDestination = options?.redirectUrl || "/create-organization";

      logger.info("auth", "signup-start", `Starting signup for email: ${email}`);

      // public.user_profiles row is created automatically by the handle_new_user
      // DB trigger on auth.users INSERT (Supabase-canonical pattern). The trigger
      // pulls first_name / last_name from raw_user_meta_data below.
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: email,
        password: password,
        options: {
          data: {
            first_name: normalizedFirstName,
            last_name: normalizedLastName,
          },
          // Thread the post-signup destination as `next` on the /auth/confirm callback
          // so the email-link click lands the user on the right page.
          emailRedirectTo: getVerificationRedirectUrl(postSignupDestination),
        },
      });

      if (authError) {
        const parsedError = parseAuthError(authError);
        logger.error("auth", "signup-auth-error", parsedError.originalError);

        toast({
          variant: "destructive",
          title: t.auth.signupFailed,
          description: parsedError.userMessage,
          action: parsedError.shouldShowLoginOption ? (
            <Button variant="outline" size="sm" onClick={() => navigate("/login")}>
              {t.auth.goToLogin}
            </Button>
          ) : undefined,
        });
        return { success: false, needsVerification: false };
      }

      if (!authData.user) {
        throw new Error("User not found after signup");
      }

      // With email confirmations on, signing up an address that ALREADY has an account
      // succeeds: GoTrue hands back an obfuscated user — a fabricated id and an empty
      // `identities` — rather than telling an anonymous caller who is registered. No
      // account was created, so neither the identity nor the event may be written off it:
      // `identify()` would pin this browser to a distinct_id that is nobody, and PostHog
      // ignores a later `identify()` with a different id until `reset()`, so the real user
      // could never be re-identified from this browser. `signed_up` is unrenameable too.
      //
      // Only an empty array is the signal; an absent one is not, so a response shape that
      // omits identities keeps reporting rather than silently going dark.
      const createdAnAccount = authData.user.identities?.length !== 0;

      // The account exists from here; the verification branch below only decides where the
      // user goes next. `has_invite` is read off the destination, which SignUp sets to the
      // invite page when the signup came from an invite link — no extra argument needed.
      if (createdAnAccount) {
        // Identity only once a SESSION exists. With confirmations on, signup returns none —
        // the address may never be confirmed — and posthog-js pins the browser to the first
        // distinct_id it is given, ignoring later `identify()` calls until `reset()`. So
        // identifying here would brand this browser with an account that may never be used,
        // and a real signup or login from it afterwards would merge into that dead identity.
        // PostHogIdentity identifies off auth state the moment a session does appear, and
        // `signed_up` captured anonymously merges into that person when it does.
        if (authData.session) identifyUser(authData.user.id);
        track("signed_up", {
          method: "password",
          has_invite: postSignupDestination.startsWith("/invite/accept"),
        });
      }

      setUserEmail(email);

      const verificationRequired = detectEmailVerificationEnabled(authData.user);

      if (verificationRequired) {
        setShowVerificationPending(true);
        setIsLoading(false);
        return { success: true, needsVerification: true };
      }

      // No verification required (dev env with mailer_autoconfirm=true, or prod
      // before enable_confirmations is flipped on). The handle_new_user trigger
      // has already created the user_profiles row — just navigate to the destination.
      logger.info("auth", "signup-success", "Signup completed successfully");

      toast({
        title: t.auth.welcome,
        description: t.auth.accountCreatedDesc,
      });

      if (!options?.skipNavigation) {
        navigate(postSignupDestination, { replace: true });
      }

      return { success: true, needsVerification: false, redirectUrl: postSignupDestination };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Unknown signup error";
      logger.error("auth", "signup-error", errorMessage);

      const parsedError = parseAuthError(error);
      toast({
        variant: "destructive",
        title: t.auth.signupFailed,
        description: parsedError.userMessage,
      });

      return { success: false, needsVerification: false };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    signUp,
    isLoading,
    showVerificationPending,
    userEmail,
    setShowVerificationPending,
  };
}
